import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, AlertTriangle, CheckCircle2, HelpCircle, PartyPopper } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { RevoraCard } from "../components/RevoraCard";
import { toast } from "sonner";

const INPUT =
  "w-full border border-black/15 bg-white px-4 py-3 text-sm rounded-sm focus:outline-none focus:ring-1 focus:ring-volt focus:border-volt transition-colors duration-200";

export default function Order() {
  const [params] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [pkgId, setPkgId] = useState(params.get("pkg") || "cards-100");
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    business_name: "",
    google_review_url: "",
    contact_name: "",
    email: "",
    website: "",
    notes: "",
  });
  const [urlState, setUrlState] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [methods, setMethods] = useState({ stripe: true, paypal: false });
  const debounce = useRef(null);

  useEffect(() => {
    api.get("/payments/methods").then((r) => setMethods(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/packages").then((r) => {
      setPackages(r.data.packages);
      if (!r.data.packages.find((p) => p.id === pkgId)) setPkgId(r.data.packages[2]?.id || r.data.packages[0]?.id);
    }).catch(() => toast.error("Could not load packages"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pkg = useMemo(() => packages.find((p) => p.id === pkgId), [packages, pkgId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onUrlChange = (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, google_review_url: v }));
    setUrlState(null);
    clearTimeout(debounce.current);
    if (v.trim().length < 8) return;
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/validate-url", { url: v });
        setUrlState(data);
      } catch { /* ignore */ }
    }, 500);
  };

  const detailsValid =
    form.business_name.trim().length >= 2 &&
    form.contact_name.trim().length >= 2 &&
    /.+@.+\..+/.test(form.email) &&
    urlState?.valid;

  const pay = async (method) => {
    if (!confirmed || submitting) return;
    setSubmitting(true);
    try {
      const { data: order } = await api.post("/orders", {
        package_id: pkgId,
        ...form,
        website: form.website || null,
        notes: form.notes || null,
        confirmed_url: confirmed,
      });
      localStorage.setItem("revora_pending_order", JSON.stringify({ order_number: order.order_number, email: order.email, business: order.business_name, quantity: order.quantity, total: order.total }));
      const { data: co } = await api.post(
        method === "paypal" ? "/payments/paypal/checkout" : "/payments/checkout",
        { order_number: order.order_number, origin_url: window.location.origin }
      );
      window.location.href = co.checkout_url || co.approve_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setSubmitting(false);
    }
  };

  if (placed) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6 py-24" data-testid="order-success">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg w-full text-center">
          <div className="w-16 h-16 rounded-full bg-volt flex items-center justify-center mx-auto mb-8">
            <PartyPopper className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-display text-4xl font-black tracking-tight text-ink">Order received.</h1>
          <p className="mt-4 text-black/55 leading-relaxed">
            Your Revora products for <strong className="text-ink">{placed.business_name}</strong> are in the queue.
            We'll configure them to route to your review page, quality-check them, and ship.
          </p>
          <div className="mt-8 bg-mist border border-black/10 rounded-sm p-6">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-black/40 mb-1">Your order number</p>
            <p className="font-display text-3xl font-black text-voltdark" data-testid="order-number">{placed.order_number}</p>
            <p className="text-xs text-black/45 mt-3">
              {placed.quantity} cards — ${placed.total.toFixed(2)} · Confirmation sent to {placed.email}
            </p>
            <p className="text-xs text-black/45 mt-2 italic">
              Payment integration is a configurable placeholder — our team will contact you at your email to complete payment.
            </p>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to={`/track?order=${placed.order_number}&email=${encodeURIComponent(placed.email)}`}
              data-testid="track-order-link"
              className="inline-flex items-center gap-2 bg-ink text-white font-bold px-7 py-3.5 rounded-full hover:bg-volt transition-colors duration-300"
            >
              Track your order <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 border border-black/15 font-bold px-7 py-3.5 rounded-full hover:border-ink transition-colors duration-300">
              Back to site
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mist" data-testid="order-page">
      <header className="bg-white border-b border-black/5">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="order-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-black/60 hover:text-ink transition-colors">
            <ArrowLeft className="w-4 h-4" /> Revora Reviews
          </Link>
          <div className="flex items-center gap-2 text-xs font-bold tracking-[0.15em] uppercase">
            {[1, 2, 3].map((n) => (
              <span key={n} className="flex items-center gap-2">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center ${step >= n ? "bg-volt text-white" : "bg-black/10 text-black/40"}`}>{n}</span>
                {n < 3 && <span className="w-6 h-px bg-black/15" />}
              </span>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }}>
                <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-ink mb-2">Choose your product</h1>
                <p className="text-black/55 text-sm mb-8">Every Revora review product includes an NFC chip, destination setup, and shipping.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="package-selector">
                  {packages.map((p) => (
                    <button
                      key={p.id}
                      data-testid={`order-package-${p.id}`}
                      onClick={() => setPkgId(p.id)}
                      className={`relative text-left p-5 rounded-sm border transition-colors duration-200 ${
                        pkgId === p.id ? "border-volt bg-white ring-1 ring-volt" : "border-black/10 bg-white hover:border-ink"
                      }`}
                    >
                      {p.featured && <span className="absolute -top-2.5 left-4 bg-volt text-ink text-[9px] font-bold tracking-[0.15em] uppercase px-2.5 py-0.5 rounded-full">Popular</span>}
                      <p className="font-display text-base font-bold text-ink leading-tight">{p.name}</p>
                      <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-black/40 mt-1">
                        {p.product === "stand" && p.quantity > 1 ? `${p.quantity} stands` : p.product === "set" ? "Stand + plate + card" : "Single item"}
                      </p>
                      <p className="font-display text-lg font-bold text-voltdark mt-2">${p.price.toFixed(2)}</p>
                    </button>
                  ))}
                </div>
                <button
                  data-testid="order-step1-next"
                  onClick={() => setStep(2)}
                  className="mt-10 inline-flex items-center gap-2 bg-ink text-white font-bold px-8 py-4 rounded-full hover:bg-volt transition-colors duration-300"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }}>
                <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-ink mb-2">Your business details</h1>
                <p className="text-black/55 text-sm mb-8">We use this to program your cards before they ship.</p>
                <div className="space-y-5">
                  <div>
                    <label className="block text-xs font-bold tracking-[0.15em] uppercase text-black/50 mb-2">Business Name *</label>
                    <input data-testid="input-business-name" className={INPUT} placeholder="e.g. Bella Vista Salon" value={form.business_name} onChange={set("business_name")} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold tracking-[0.15em] uppercase text-black/50 mb-2">Google Review URL *</label>
                    <div className="relative">
                      <input
                        data-testid="input-review-url"
                        className={`${INPUT} pr-10 ${urlState && !urlState.valid ? "border-red-400" : ""}`}
                        placeholder="https://g.page/r/your-business/review"
                        value={form.google_review_url}
                        onChange={onUrlChange}
                      />
                      {urlState?.valid && (
                        <CheckCircle2 className={`absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 ${urlState.looks_like_google ? "text-green-500" : "text-amber-500"}`} />
                      )}
                    </div>
                    <p className="text-xs text-black/45 mt-2">This is the page customers will reach when they tap your Revora stand.</p>
                    {urlState && !urlState.valid && (
                      <p data-testid="url-error" className="text-xs text-red-500 mt-2 flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" /> This doesn't look like a valid URL.
                      </p>
                    )}
                    {urlState?.warning && (
                      <p data-testid="url-warning" className="text-xs text-amber-600 mt-2 flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" /> {urlState.warning}
                      </p>
                    )}
                    <button
                      data-testid="toggle-url-guide"
                      onClick={() => setShowGuide(!showGuide)}
                      className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-voltdark hover:text-ink transition-colors"
                    >
                      <HelpCircle className="w-3.5 h-3.5" /> Don't know where to find your Google review link?
                    </button>
                    <AnimatePresence>
                      {showGuide && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                          data-testid="url-guide"
                        >
                          <ol className="mt-3 bg-white border border-black/10 rounded-sm p-5 space-y-2.5 text-sm text-black/60 list-decimal list-inside">
                            <li>Search your business name on Google while signed in to your Business Profile.</li>
                            <li>Open your profile and select <strong className="text-ink">"Ask for reviews"</strong> (or "Read reviews" → "Get more reviews").</li>
                            <li>Copy the share link — it usually looks like <span className="font-mono text-xs bg-mist px-1.5 py-0.5 rounded">https://g.page/r/…/review</span>.</li>
                            <li>Paste it above. Test it in an incognito window to make sure it opens your review form.</li>
                          </ol>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold tracking-[0.15em] uppercase text-black/50 mb-2">Contact Name *</label>
                      <input data-testid="input-contact-name" className={INPUT} placeholder="Your name" value={form.contact_name} onChange={set("contact_name")} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold tracking-[0.15em] uppercase text-black/50 mb-2">Contact Email *</label>
                      <input data-testid="input-email" type="email" className={INPUT} placeholder="you@business.com" value={form.email} onChange={set("email")} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold tracking-[0.15em] uppercase text-black/50 mb-2">Website <span className="text-black/30 normal-case tracking-normal">(optional)</span></label>
                    <input data-testid="input-website" className={INPUT} placeholder="https://yourbusiness.com" value={form.website} onChange={set("website")} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold tracking-[0.15em] uppercase text-black/50 mb-2">Notes <span className="text-black/30 normal-case tracking-normal">(optional)</span></label>
                    <textarea data-testid="input-notes" className={`${INPUT} min-h-[90px] resize-y`} placeholder="Anything we should know about your order" value={form.notes} onChange={set("notes")} />
                  </div>
                </div>
                <div className="mt-10 flex gap-3">
                  <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 border border-black/15 font-bold px-7 py-4 rounded-full hover:border-ink transition-colors duration-300">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <button
                    data-testid="order-step2-next"
                    onClick={() => setStep(3)}
                    disabled={!detailsValid}
                    className="inline-flex items-center gap-2 bg-ink text-white font-bold px-8 py-4 rounded-full hover:bg-volt disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-300"
                  >
                    Review order <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }}>
                <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-ink mb-2">Confirm your review destination</h1>
                <p className="text-black/55 text-sm mb-8">One quick quality-control step — your cards are programmed exactly to this link.</p>

                <div className="bg-ink text-white rounded-sm p-7 mb-6" data-testid="url-confirmation-box">
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-3">Your products will route customers to:</p>
                  <p className="font-mono text-sm sm:text-base text-volt break-all bg-white/5 border border-white/10 rounded-sm px-4 py-3" data-testid="confirmed-url">
                    {form.google_review_url}
                  </p>
                  <p className="text-xs text-white/50 mt-3">Please confirm this is the correct review page before placing your order.</p>
                </div>

                <div className="bg-white border border-black/10 rounded-sm p-6 mb-6 text-sm space-y-2">
                  <div className="flex justify-between"><span className="text-black/50">Product</span><span className="font-bold text-ink">{pkg?.name}{pkg?.quantity > 1 ? ` × ${pkg.quantity}` : ""}</span></div>
                  <div className="flex justify-between"><span className="text-black/50">Business</span><span className="font-bold text-ink">{form.business_name}</span></div>
                  <div className="flex justify-between"><span className="text-black/50">Contact</span><span className="font-bold text-ink">{form.contact_name} · {form.email}</span></div>
                  <div className="flex justify-between border-t border-black/10 pt-2 mt-2"><span className="text-black/50">Total (incl. programming & shipping)</span><span className="font-display text-xl font-black text-volt">${pkg?.price.toFixed(2)}</span></div>
                </div>

                <div className="bg-mist border border-black/10 rounded-sm p-5 mb-6 text-xs text-black/55 leading-relaxed" data-testid="order-plan-notice">
                  Your products work with the free Revora business portal (<strong className="text-ink">Starter</strong>) — after checkout, create an account with this same email to see them. Starter includes a fixed review URL: no analytics and no destination editing. <strong className="text-ink">Essential</strong> ($9.99/mo, 3 registered assets) and <strong className="text-ink">Growth</strong> ($19.99/mo, 10 registered assets) add destination editing and tap analytics; active subscriptions can add slots at $1 per additional registered asset per month. Every product routes to this destination regardless of plan. Revora reports taps on your products, not confirmed Google reviews.
                </div>

                <label className="flex items-start gap-3 bg-white border border-black/10 rounded-sm p-5 cursor-pointer mb-8" data-testid="confirm-checkbox-label">
                  <input
                    type="checkbox"
                    data-testid="confirm-url-checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#0055FF]"
                  />
                  <span className="text-sm text-black/70">
                    I confirm that the Google review URL I provided is correct. I understand my products will route
                    customers to this link — and that the destination can be updated later without replacing them.
                  </span>
                </label>

                <div className="flex flex-wrap gap-3">
                  <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 border border-black/15 font-bold px-7 py-4 rounded-full hover:border-ink transition-colors duration-300">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <button
                    data-testid="pay-card-button"
                    onClick={() => pay("stripe")}
                    disabled={!confirmed || submitting}
                    className="inline-flex items-center gap-2 bg-volt text-ink font-bold px-8 py-4 rounded-full hover:bg-voltdark hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-300"
                  >
                    {submitting ? "Redirecting…" : `Pay $${pkg?.price.toFixed(2)} by Card`} <Check className="w-4 h-4" />
                  </button>
                  {methods.paypal && (
                    <button
                      data-testid="pay-paypal-button"
                      onClick={() => pay("paypal")}
                      disabled={!confirmed || submitting}
                      className="inline-flex items-center gap-2 bg-[#FFC439] text-ink font-bold px-8 py-4 rounded-full hover:brightness-95 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-300"
                    >
                      Pay with PayPal
                    </button>
                  )}
                </div>
                <p className="text-xs text-black/40 mt-4">Secure checkout by Stripe{methods.paypal ? " or PayPal" : ""}. Your order is recorded before payment and confirmed the moment payment completes.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <aside className="lg:col-span-5">
          <div className="sticky top-10 bg-white border border-black/10 rounded-sm p-8">
            <div className="w-40 mx-auto mb-6">
              <RevoraCard tilt={false} float={false} />
            </div>
            <p className="font-display text-xl font-bold text-ink text-center">{pkg?.name || "Revora Review Products"}</p>
            <p className="text-xs text-black/45 text-center mt-1 mb-6">Configured for your business before they ship</p>
            {pkg && (
              <div className="flex justify-between items-center border-t border-black/10 pt-5">
                <span className="text-sm text-black/55">{pkg.name}{pkg.quantity > 1 ? ` × ${pkg.quantity}` : ""}</span>
                <span className="font-display text-2xl font-black text-ink" data-testid="order-summary-price">${pkg.price.toFixed(2)}</span>
              </div>
            )}
            <ul className="mt-5 space-y-2">
              {["NFC chip", "NFC programming / setup", "Shipping"].map((f) => (
                <li key={f} className="flex items-center gap-2 text-xs text-black/55">
                  <Check className="w-3.5 h-3.5 text-volt" /> {f}
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </main>
    </div>
  );
}
